import { Checkbox, Col, Collapse, Form, InputNumber, Row, Switch } from "antd";
import CollapsePanel from "antd/es/collapse/CollapsePanel";
import { useForm } from "antd/es/form/Form";
import { DefaultOptionType } from "antd/es/select";
import { FC, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { FormCollapseHeader } from "../../../../components/FormCollapseHeader/FormCollapseHeader";
import { Input } from "../../../../components/Input/Input";
import { Modal } from "../../../../components/Modal/Modal";
import { Select } from "../../../../components/Select/Select";
import { ModalsContext } from "../../../../context/modals.context";
import {
  ICompanyObject,
  ITipObjekta,
} from "../../../../modules/settings/settings.types";
import { mapSelectOptions } from "../../../../utils/map4select";

import { MandatoryField } from "../../../../components/MandatoryField/MandatoryField";
import style from "./CompanyObjectModal.module.scss";

const masineEmptyState = {
  model: "",
  proizvodjac: "",
  serijskiBroj: "",
  najveciKapacitet: "",
  aktivno: true,
};

interface IObjectTypeOptions extends DefaultOptionType, ITipObjekta {}
export interface ICompanyObjectModalProps {
  selectedObject: ICompanyObject | null;
  clearObject: () => void;
  editCompanyObject: (user: ICompanyObject) => void;
  createCompanyObject: (user: ICompanyObject) => void;
  refetchObjects?: () => void;
  objectTypes: IObjectTypeOptions[];
}

export const CompanyObjectModal: FC<ICompanyObjectModalProps> = ({
  selectedObject,
  clearObject,
  editCompanyObject,
  createCompanyObject,
  refetchObjects,
  objectTypes,
}) => {
  const { isModalVisible, clearModals } = useContext(ModalsContext);
  const { t } = useTranslation();
  const [form] = useForm();
  const isAkciznoSkladiste = Form.useWatch("akciznoSkladiste", form);
  const isCarinskoSkladiste = Form.useWatch("carinskoSkladiste", form);
  const selectedTipId = Form.useWatch("tipId", form);
  const selectedTip = objectTypes.find(
    (oType) => oType.value === selectedTipId
  );
  const hasMasine = selectedTip?.hasMasine;
  const isVozilo = selectedTip?.hasVozilo;

  const handleOk = () => {
    form.submit();
    if (refetchObjects) refetchObjects();
    clearModals();
  };

  const handleCancel = () => {
    form.resetFields();
    if (selectedObject) clearObject();
    clearModals();
  };

  const handleSubmit = (values: ICompanyObject) => {
    selectedObject
      ? editCompanyObject({ ...values, id: selectedObject.id })
      : createCompanyObject(values);

    setTimeout(() => {
      form.resetFields();
      if (selectedObject) clearObject();
    }, 350);
  };

  const title = !selectedObject
    ? t("SETTINGS_OBJECTS.NOVI_OBJEKAT")
    : selectedObject.naziv;

  const skladistaOptions = mapSelectOptions({
    sourceArray: selectedTip?.akciznaSkladista || [],
    labelAccessor: "naziv",
    valueAccessor: "id",
  });

  function generateMasinaLabel(
    serijskiBroj: string | undefined,
    id: number | undefined,
    index: number
  ) {
    if (serijskiBroj && id) return `${id} - ${serijskiBroj}`;
    if (!serijskiBroj && id) return `${id}`;

    return serijskiBroj
      ? serijskiBroj
      : t("SETTINGS_OBJECTS.MASINA_BR", {
          br: index + 1,
        });
  }

  useEffect(() => {
    if (!hasMasine && !selectedObject) {
      form.setFieldsValue({
        masine: [masineEmptyState],
        akciznoSkladisteId: undefined,
        akciznoSkladiste: undefined,
        carinskoSkladiste: undefined,
      });
    }
  }, [hasMasine, selectedObject, form]);

  useEffect(() => {
    if (selectedObject) {
      form.setFieldsValue({
        ...selectedObject,
        masine: selectedObject.masine?.length
          ? selectedObject.masine
          : [{ aktivno: true }],
      });
    } else {
      form.resetFields();
    }
  }, [selectedObject, form]);

  useEffect(() => {
    if (!isAkciznoSkladiste && !selectedObject) {
      form.setFieldsValue({ akciznoSkladisteId: undefined });
    }
  }, [isAkciznoSkladiste, selectedObject, form]);

  useEffect(() => {
    if (!isVozilo)
      form.setFieldsValue({
        voziloOznaka: undefined,
      });
  }, [isVozilo, form]);

  return (
    <Modal
      name="company-user-modal"
      title={
        <Row>
          <Col span={14}>{title}</Col>
        </Row>
      }
      onOk={handleOk}
      cancelText={t("SETTINGS_COMPANY.NAZAD")}
      width={1350}
      cancelButtonProps={{ type: "default" }}
      okText={t("SETTINGS_COMPANY.SACUVAJ")}
      onCancel={handleCancel}
      open={isModalVisible("company_object")}
      destroyOnClose
      forceRender
    >
      <Form
        form={form}
        labelCol={{ span: 8 }}
        labelAlign="right"
        onFinish={handleSubmit}
        initialValues={
          selectedObject
            ? selectedObject
            : { masine: [{ aktivno: true }], aktivno: true }
        }
      >
        <div className={style.form}>
          <div className="w-49">
            <Row>
              <Col span={16}>
                <Form.Item
                  labelCol={{ span: 6 }}
                  wrapperCol={{ span: 12 }}
                  name="tipId"
                  colon={false}
                  label={MandatoryField("COMMON.TIP")}
                >
                  <Select name="object-name" options={objectTypes} />
                </Form.Item>

                {isAkciznoSkladiste && (
                  <Form.Item
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 12 }}
                    name="akciznoSkladisteId"
                    colon={false}
                    label={MandatoryField("SETTINGS_OBJECTS.SKLADISTE")}
                  >
                    {/* Change to akcizno skladiste options */}
                    <Select
                      name="object-name"
                      options={skladistaOptions}
                      placeholder={t("PLACEHOLDERS.IZABERI_AKCIZNO_SKLADISTE")}
                    />
                  </Form.Item>
                )}
              </Col>

              <Col span={8}>
                <Row>
                  <Col span={24}>
                    {selectedTip?.hasAkciznoSkladiste && (
                      <Form.Item
                        labelCol={{ span: 22 }}
                        className="w-100"
                        name="akciznoSkladiste"
                        colon={false}
                        valuePropName="checked"
                        label={t("SETTINGS_OBJECTS.AKCIZNO_SKLADISTE")}
                      >
                        <Checkbox
                          disabled={isCarinskoSkladiste}
                          name="object-akcizno-skladiste"
                        />
                      </Form.Item>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    {selectedTip?.hasCarinskoSkladiste && (
                      <Form.Item
                        labelCol={{ span: 22 }}
                        className="w-100"
                        name="carinskoSkladiste"
                        colon={false}
                        valuePropName="checked"
                        label={t("SETTINGS_OBJECTS.CARINSKO_SKLADISTE")}
                      >
                        <Checkbox
                          disabled={isAkciznoSkladiste}
                          name="object-carinsko-skladiste"
                        />
                      </Form.Item>
                    )}
                  </Col>
                </Row>
              </Col>
              {/*)}*/}
            </Row>
            <Row>
              <Col span={16}>
                {!isVozilo && (
                  <Form.Item
                    labelCol={{ span: 6 }}
                    name="naziv"
                    colon={false}
                    label={MandatoryField("COMMON.NAZIV")}
                  >
                    <Input name="object-name" />
                  </Form.Item>
                )}
                {isVozilo && (
                  <Form.Item
                    labelCol={{ span: 6 }}
                    name="voziloOznaka"
                    colon={false}
                    label={MandatoryField("COMMON.OZNAKA")}
                  >
                    <Input name="object-vozilo-oznaka" />
                  </Form.Item>
                )}
              </Col>
              <Col span={8}>
                <Form.Item
                  name="aktivno"
                  colon={false}
                  labelCol={{ span: 12 }}
                  label={t("COMMON.AKTIVNO")}
                  valuePropName="checked"
                >
                  <Switch defaultChecked />
                </Form.Item>
              </Col>
            </Row>

            {!isVozilo && (
              <>
                <h4 className={style.sectionTitle}>{t("COMMON.ADRESA")}</h4>
                <Row>
                  <Col span={12}>
                    <Form.Item
                      name="zemlja"
                      colon={false}
                      label={MandatoryField("COMMON.ZEMLJA")}
                    >
                      <Input name="object-zemlja" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col span={12}>
                    <Form.Item
                      name="grad"
                      colon={false}
                      label={MandatoryField("COMMON.GRAD")}
                    >
                      <Input name="object-city" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="postanskiBroj"
                      colon={false}
                      label={MandatoryField("COMMON.POSTANSKI_BROJ")}
                    >
                      <Input name="object-post-number" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col span={12}>
                    <Form.Item
                      name="ulica"
                      colon={false}
                      label={MandatoryField("COMMON.ULICA")}
                    >
                      <Input name="object-post-number" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="broj"
                      colon={false}
                      label={MandatoryField("COMMON.BROJ")}
                    >
                      <Input name="object-post-number" />
                    </Form.Item>
                  </Col>
                </Row>

                <h4 className={style.sectionTitle}>
                  {t("SETTINGS_OBJECTS.ODGOVORNO_LICE")}
                </h4>
                <Row>
                  <Col span={12}>
                    <Form.Item
                      name="odgovornoLiceIme"
                      colon={false}
                      label={MandatoryField("COMMON.IME")}
                    >
                      <Input name="object-post-number" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="odgovornoLicePrezime"
                      colon={false}
                      label={MandatoryField("COMMON.PREZIME")}
                    >
                      <Input name="object-post-number" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col span={12}>
                    <Form.Item
                      name="odgovornoLiceEmail"
                      colon={false}
                      label={MandatoryField("COMMON.EMAIL")}
                    >
                      <Input name="object-post-number" />
                    </Form.Item>
                  </Col>
                  <Col span={12}></Col>
                </Row>
              </>
            )}
          </div>
          {hasMasine && (
            <div className={style.masineWrapper}>
              {hasMasine && <Col span={10}>{t("SETTINGS_OBJECTS.MASINE")}</Col>}
              <Form.List name="masine">
                {(fields, { add, remove }) => (
                  <Collapse accordion>
                    {fields.map((field, i) => {
                      return (
                        <CollapsePanel
                          key={i}
                          header={
                            <FormCollapseHeader
                              i={i}
                              fields={fields}
                              add={() => {
                                add({
                                  aktivno: true,
                                  model: undefined,
                                  najveciKapacitet: undefined,
                                  proizvodjac: undefined,
                                  serijskiBroj: undefined,
                                });
                              }}
                              canDelete={
                                !form.getFieldValue(["masine", i, "id"])
                              }
                              remove={remove}
                              label={generateMasinaLabel(
                                form.getFieldValue([
                                  "masine",
                                  i,
                                  "serijskiBroj",
                                ]),
                                form.getFieldValue(["masine", i, "id"]),
                                i
                              )}
                            />
                          }
                        >
                          <>
                            <Row className={style.masineItem}>
                              <Col span={16}>
                                {form.getFieldValue(["masine", i, "id"]) ? (
                                  <Form.Item
                                    labelCol={{ span: 6 }}
                                    name={[field.name, "id"]}
                                    colon={false}
                                    label={t("SETTINGS_OBJECTS.ID_MASINE")}
                                  >
                                    <Input
                                      placeholder={t(
                                        "SETTINGS_OBJECTS.ID_MASINE"
                                      )}
                                      name="object-masina-id"
                                      disabled
                                    />
                                  </Form.Item>
                                ) : (
                                  <></>
                                )}
                              </Col>
                              <Col span={16}>
                                <Form.Item
                                  labelCol={{ span: 6 }}
                                  name={[field.name, "model"]}
                                  colon={false}
                                  label={MandatoryField(
                                    "SETTINGS_OBJECTS.MODEL"
                                  )}
                                >
                                  <Input
                                    placeholder={t("SETTINGS_OBJECTS.MODEL")}
                                    name="object-masina-model"
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item
                                  name={[field.name, "aktivno"]}
                                  colon={false}
                                  labelCol={{ span: 12 }}
                                  label={t("COMMON.AKTIVNO")}
                                  valuePropName="checked"
                                >
                                  <Switch defaultChecked />
                                </Form.Item>
                              </Col>
                            </Row>
                            <Row className={style.masineItem}>
                              <Col span={16}>
                                <Form.Item
                                  labelCol={{ span: 6 }}
                                  name={[field.name, "proizvodjac"]}
                                  colon={false}
                                  label={MandatoryField(
                                    "SETTINGS_OBJECTS.PROIZVODJAC"
                                  )}
                                >
                                  <Input
                                    placeholder={t(
                                      "SETTINGS_OBJECTS.PROIZVODJAC"
                                    )}
                                    name="object-masina-proizvodjac"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                            <Row className={style.masineItem}>
                              <Col span={16}>
                                <Form.Item
                                  labelCol={{ span: 6 }}
                                  name={[field.name, "serijskiBroj"]}
                                  colon={false}
                                  label={MandatoryField(
                                    "SETTINGS_OBJECTS.SERIJSKI_BR"
                                  )}
                                >
                                  <Input
                                    placeholder={t(
                                      "SETTINGS_OBJECTS.SERIJSKI_BR"
                                    )}
                                    name="object-masina-serijski-br"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                            <Row className={style.masineItem}>
                              <Col span={4}></Col>
                              <Col span={16}>
                                {MandatoryField(
                                  "SETTINGS_OBJECTS.NAJVECI_KAPACITET"
                                )}
                              </Col>
                              <Col span={16}>
                                <Form.Item
                                  labelCol={{ span: 6 }}
                                  name={[field.name, "najveciKapacitet"]}
                                  colon={false}
                                  label={" "}
                                >
                                  <InputNumber
                                    style={{ width: "120px" }}
                                    min={0}
                                    placeholder={t(
                                      "SETTINGS_OBJECTS.KAPACITET"
                                    )}
                                    name="object-masina-najveci-kapacitet "
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </>
                        </CollapsePanel>
                      );
                    })}
                  </Collapse>
                )}
              </Form.List>
            </div>
          )}
        </div>
      </Form>
    </Modal>
  );
};
